import React from 'react';
import moment from 'moment';
import {
  PulseVideoCamera,
  PulseVideoCameraSetupStatus,
  TimeSeriesInputInterval,
  useGetJobsitePulseDataQuery,
  useGetJobsitePulseTimeSeriesQuery,
  useGetJobsitePulseVideoFilesQuery,
  useGetJobsitePulseVideosQuery,
} from 'apollo/generated/client-operations';
import { getApiBaseUrl } from 'config/app';
import {
  getDateTimeMoment,
  getJobsiteDateTime,
  getISOFormattedDate,
} from 'utils/dates';
import {
  JobsitePulseActivity,
  JobsitePulseCamera,
  JobsitePulseCameraThumbnail,
  JobsitePulseData,
  JobsitePulseState,
} from './types';

export type UseJobsitePulseDataArgs = {
  jobsiteId: string;
  date: moment.Moment;
  time: string;
  skip?: boolean;
};

export function useJobsitePulseData(
  args: UseJobsitePulseDataArgs,
): JobsitePulseState {
  const { date, jobsiteId, time, skip } = args;

  // Calculate startTime and endTime as 15 minutes before and after the provided time
  const startTime = moment(time, 'hh:mm A')
    .subtract(15, 'minutes')
    .format('hh:mm A');
  const endTime = moment(time, 'hh:mm A')
    .add(14, 'minutes')
    .format('hh:mm:ss.SSS A');

  const startDateTime = getJobsiteDateTime({ date, time: startTime });
  const endDateTime = getJobsiteDateTime({ date, time: endTime, isEnd: true });

  const startOfDate = moment.utc(date).startOf('day').toDate();
  const endOfDate = moment.utc(date).endOf('day').toDate();

  const {
    data: jobsitePulseData,
    loading: jobsitePulseLoading,
    error: jobsitePulseError,
    refetch: refetchJobsitePulseData,
  } = useGetJobsitePulseDataQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skip || !jobsiteId,
    variables: {
      jobsiteId,
      jobsiteAccessEventsInput: {
        startDateTime,
        endDateTime,
      },
      jobsiteFormSubmissionsInput: {
        startDate: startOfDate,
        endDate: endOfDate,
        formKeys: ['site-safety', 'daily-log', 'safety-observation'],
      },
    },
  });

  const {
    data: timeSeriesData,
    loading: timeSeriesLoading,
    error: timeSeriesError,
    refetch: refetchTimeSeries,
  } = useGetJobsitePulseTimeSeriesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skip || !jobsiteId,
    variables: {
      jobsiteId,
      jobsiteAccessSessionsInput: {
        startDate: startOfDate,
        endDate: endOfDate,
      },
      timeSeriesInput: {
        startDateTime: startOfDate,
        endDateTime: endOfDate,
        interval: TimeSeriesInputInterval.Every_15Minutes,
      },
      weatherInput: {
        dates: [getISOFormattedDate(date)],
        createIfNotExists: true,
      },
    },
  });

  const {
    data: pulseVideosData,
    loading: pulseVideosLoading,
    error: pulseVideosError,
    refetch: refetchPulseVideosData,
  } = useGetJobsitePulseVideosQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skip || !jobsiteId,
    variables: { jobsiteId },
  });

  // Run the uploaded-video fallback only when Avigilon cameras are disabled:
  // pulseVideos is non-null only when enabled, so skip then (and on error).
  // Shared with the refetch guard below to keep the two in sync.
  const fileVideosSkipped =
    skip ||
    !jobsiteId ||
    pulseVideosLoading ||
    !!pulseVideosError ||
    pulseVideosData?.getJobsite?.pulseVideos != null;

  const {
    data: pulseVideoFilesData,
    loading: pulseVideoFilesLoading,
    error: pulseVideoFilesError,
    refetch: refetchPulseVideoFilesData,
  } = useGetJobsitePulseVideoFilesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: fileVideosSkipped,
    variables: { jobsiteId },
  });

  const selectedTimeMoment = getDateTimeMoment({ date, time });
  const timeSeriesStateData = React.useMemo((): Pick<
    JobsitePulseData,
    'dailyTotalWorkers' | 'timeSeries' | 'weatherConditions' | 'workersOnSite'
  > => {
    const { jobsiteAccessSessions, timeSeries, weather } =
      timeSeriesData?.getJobsite ?? {};
    const { weatherConditions } = weather?.edges?.[0]?.node ?? {};

    return {
      dailyTotalWorkers: jobsiteAccessSessions?.count ?? 0,
      timeSeries,
      weatherConditions,
      workersOnSite:
        timeSeries?.sessions.find((session) =>
          moment(session.dateTime).isSameOrAfter(selectedTimeMoment),
        )?.value ?? 0,
    };
  }, [timeSeriesData, selectedTimeMoment?.toISOString()]);

  const jobsitePulseStateData = React.useMemo((): Omit<
    JobsitePulseData,
    | 'cameras'
    | 'dailyTotalWorkers'
    | 'timeSeries'
    | 'weatherConditions'
    | 'workersOnSite'
  > => {
    const { jobsiteAccessEvents, jobsiteFormSubmissions, timeZone } =
      jobsitePulseData?.getJobsite ?? {};

    return {
      accessEvents: jobsiteAccessEvents?.edges.map(({ node }) => node) ?? [],
      activityList: jobsiteFormSubmissions?.edges.map(
        ({ node }): JobsitePulseActivity => {
          const { id, startAt, extraData, jobsiteForm } = node;
          const { name, description, workPerformed, observationType } =
            extraData ?? {};
          const { form } = jobsiteForm ?? {};

          const getLabelAndDescription = (): Pick<
            JobsitePulseActivity,
            'label' | 'description'
          > => {
            switch (form?.key) {
              case 'daily-log':
                return { label: form.name, description: workPerformed };
              case 'safety-observation':
                return { label: observationType ?? form.name, description };
              case 'site-safety':
              default:
                return { label: name ?? form.name, description };
            }
          };

          return {
            id,
            date: moment(startAt).format('LL'),
            time: moment(startAt).format('hh:mm A'),
            isIncident: form?.key === 'site-safety',
            ...getLabelAndDescription(),
          };
        },
      ),
      date: moment(date).format('LL'),
      time,
      shortTimeZone:
        timeZone && getDateTimeMoment({ date, time, timeZone }).format('z'),
    };
  }, [jobsitePulseData, date?.toISOString(), time]);

  const pulseCamerasStateData = React.useMemo((): Pick<
    JobsitePulseData,
    'cameras' | 'hlsBasicAuth'
  > => {
    const pulseVideos = pulseVideosData?.getJobsite?.pulseVideos;
    if (!pulseVideos) return { cameras: [], hlsBasicAuth: undefined };

    const { avigilonUrl, hlsBasicAuth, cameras } = pulseVideos;
    const apiBaseUrl = getApiBaseUrl();

    // HLS service is always on alta.avigilon.com, regardless of the API domain.
    // Transform: odin.us4.aware.avasecurity.com → odin.stream.us4.alta.avigilon.com
    const parsedUrl = new URL(avigilonUrl);
    const [deployment, region] = parsedUrl.hostname.split('.');
    const hlsBaseUrl = `${parsedUrl.protocol}//${deployment}.stream.${region}.alta.avigilon.com`;
    const isWithinTheLastMinuteOrAfter = selectedTimeMoment.isSameOrAfter(
      moment().subtract(1, 'minute'),
    );
    const streamMode = isWithinTheLastMinuteOrAfter ? 'streams' : 'playback';
    const isLive = streamMode === 'streams';
    const startTimestampParam = !isLive
      ? `&startTimestamp=${selectedTimeMoment.toISOString()}`
      : '';

    const getCameraThumbnails = (
      camera: PulseVideoCamera,
      source: string,
    ): JobsitePulseCameraThumbnail[] => {
      // return thumbnails for each hour between 6am and 6pm in the jobsite's timezone
      return Array.from({ length: 12 }, (_, i) => i + 6)
        .map((hour) =>
          selectedTimeMoment.clone().startOf('day').add(hour, 'hours'),
        )
        .filter((thumbnailTime) => !thumbnailTime.isAfter(moment()))
        .map((thumbnailTime) => ({
          id: `${camera.deviceId}-thumbnail-${thumbnailTime.hour()}`,
          url: `${apiBaseUrl}/api/avigilon/thumbnail?deviceId=${
            camera.deviceId
          }&source=${source}&time=${thumbnailTime.toISOString()}`,
        }));
    };

    return {
      hlsBasicAuth,
      cameras: cameras.map((camera): JobsitePulseCamera => {
        const { deviceId, name, sources, oldestRecording, setupStatus } =
          camera;
        const source = sources[0] ?? '1';

        const getHasFootage = (): boolean | null => {
          if (isLive) {
            return true;
          }
          if (oldestRecording) {
            return selectedTimeMoment.isSameOrAfter(moment(oldestRecording));
          }
          if (
            setupStatus &&
            setupStatus !== PulseVideoCameraSetupStatus.Complete
          ) {
            return false;
          }
          return null;
        };

        return {
          id: deviceId,
          name,
          location: '',
          isLive,
          oldestRecording,
          hasFootage: getHasFootage(),
          url: `${hlsBaseUrl}/hls/${streamMode}/index?deviceId=${deviceId}&source=${source}${startTimestampParam}`,
          currentThumbnailUrl: isLive
            ? `${apiBaseUrl}/api/avigilon/currentThumbnail?deviceId=${deviceId}&source=${source}`
            : `${apiBaseUrl}/api/avigilon/thumbnail?deviceId=${deviceId}&source=${source}&time=${selectedTimeMoment.toISOString()}`,
          thumbnails: getCameraThumbnails(camera, source),
        };
      }),
    };
  }, [pulseVideosData, selectedTimeMoment.toISOString()]);

  // File-based fallback cameras, mapped into the current JobsitePulseCamera shape.
  const fileCamerasData = React.useMemo((): Pick<
    JobsitePulseData,
    'cameras'
  > => {
    const { pulseVideoFiles } = pulseVideoFilesData?.getJobsite ?? {};

    return {
      cameras:
        pulseVideoFiles?.map((file, index): JobsitePulseCamera => {
          const { videoFile, thumbnailFiles } = file;
          return {
            id: `pulse-video-${index + 1}`,
            name: `Camera ${index + 1}`,
            location: '',
            isLive: false,
            hasFootage: true,
            oldestRecording: '',
            url: videoFile?.downloadUrl ?? '',
            currentThumbnailUrl: thumbnailFiles?.[0]?.downloadUrl ?? '',
            thumbnails: thumbnailFiles?.map((thumbnail) => ({
              id: thumbnail.fileName,
              url: thumbnail.downloadUrl,
            })),
            type: 'file',
          };
        }) ?? [],
    };
  }, [pulseVideoFilesData]);

  const [selectedCameraId, setSelectedCameraId] = React.useState<string | null>(
    null,
  );

  const refetch = React.useCallback((): void => {
    refetchJobsitePulseData();
    refetchTimeSeries();
    refetchPulseVideosData();
    if (!fileVideosSkipped) {
      refetchPulseVideoFilesData();
    }
  }, [
    refetchJobsitePulseData,
    refetchTimeSeries,
    refetchPulseVideosData,
    refetchPulseVideoFilesData,
    fileVideosSkipped,
  ]);

  return React.useMemo((): JobsitePulseState => {
    // Avigilon cameras take precedence; fall back to uploaded file cameras.
    const avigilonStateCameras = pulseCamerasStateData.cameras;
    const cameras = avigilonStateCameras.length
      ? avigilonStateCameras
      : fileCamerasData.cameras;
    const selectedCamera =
      (selectedCameraId
        ? cameras.find((cam) => cam.id === selectedCameraId)
        : cameras[0]) ?? null;

    return {
      loading: {
        jobsitePulse: jobsitePulseLoading,
        timeSeries: timeSeriesLoading,
        pulseVideos: pulseVideosLoading || pulseVideoFilesLoading,
      },
      error: {
        jobsitePulse: jobsitePulseError,
        timeSeries: timeSeriesError,
        pulseVideos: pulseVideosError ?? pulseVideoFilesError,
      },
      refetch,
      selectedCamera,
      selectCamera: setSelectedCameraId,
      ...jobsitePulseStateData,
      ...timeSeriesStateData,
      ...pulseCamerasStateData,
      cameras,
    };
  }, [
    jobsitePulseLoading,
    jobsitePulseError,
    timeSeriesLoading,
    timeSeriesError,
    pulseVideosLoading,
    pulseVideosError,
    pulseVideoFilesLoading,
    pulseVideoFilesError,
    jobsitePulseStateData,
    timeSeriesStateData,
    pulseCamerasStateData,
    fileCamerasData,
    selectedCameraId,
    setSelectedCameraId,
    refetch,
  ]);
}
